import {Component, Input, OnInit} from '@angular/core';
import {
    FraudReportApiService, FraudReportDataPaginatedResponse,
    FraudReportResponse, FraudReportSection,
    ReferenceBookResponse,
    TransactionType,
} from '@npck/af-frontend-api';
import {DATE_TIME_SERVER_FORMAT, NESTED_TAB, RouterService, rxSpinner} from '@npck/libs/commons';
import {
    fieldExtractor,
    FilterDictionaryField,
    FilterField,
    getFormattedDateOrNull,
} from '../../../../../components/full-table/filter/filter.component';
import {initialTableEvent, mergeTableEvent, TableEvent} from '../../../../../components/full-table/full-table.component';
import {Column, ColumnType, Row, StatusColumn} from '../../../../../components/full-table/table/table.component';
import {mapRefundStatusToStyle, mapTransactionStatusToStyle} from '../../../../../components/status/status.component';
import {findValueInDictionary} from '../../../../../domain/dictionary-holder.service';
import {FraudReportDetailsFilterHolderService} from '../../../../../domain/fraud-report-details-filter-holder.service';
import {INCIDENT_ID, REFUND_ID, TRANSACTION_ID, TRANSACTION_INDEX} from '../../../../../domain/query-params.keys';
import {Path} from '../../../../../path';
import {RefundStatuses} from '../../../../refunds/refunds.component';
import {FraudReportDetailsTab} from '../fraud-report-details.component';

@Component({
    selector: 'af-fraud-report-data-tab',
    templateUrl: './fraud-report-data-tab.component.html',
    styleUrl: './fraud-report-data-tab.component.scss',
    standalone: false,
})
export class FraudReportDataTabComponent implements OnInit {
    @Input() fraudReport: FraudReportResponse;
    @Input() transactionTypes: ReferenceBookResponse[];
    @Input() transactionCategories: ReferenceBookResponse[];
    @Input() transactionStatuses: ReferenceBookResponse[];
    @Input() currencies: ReferenceBookResponse[];
    @Input() refundTypes: ReferenceBookResponse[];
    @Input() refundStatuses: ReferenceBookResponse[];
    @Input() reportSection: string;
    filterFields: FilterField[] = [
        {
            type: 'text',
            name: 'query',
        },
        <FilterField>{
            type: 'dateRange',
            name: 'separatedDateRange',
        },
        <FilterDictionaryField>{
            isMultiSelect: true,
            type: 'select',
            name: 'transactionTypes',
            dictionaryName: 'transaction_type',
            values: [],
        },
        <FilterField>{
            type: 'amountRange',
            name: 'amountRange',
            translationPrefix: 'fraudReport'
        },
    ];
    dataSource: Row[] = [];
    tableEvent: TableEvent;
    totalElements: number = 0;
    totalAmount: number = 0;
    response: FraudReportDataPaginatedResponse;
    currentTab: FraudReportDetailsTab;

    constructor(
        private fraudReportApiService: FraudReportApiService,
        public filterHolderService: FraudReportDetailsFilterHolderService,
        private routerService: RouterService,
    ) {
    }

    ngOnInit(): void {
        this.currentTab = ('tab-' + this.reportSection) as FraudReportDetailsTab;
        this.loadReportData(this.filterHolderService.getState(this.fraudReport.id, this.currentTab)?.tableEvent
            ?? this.buildInitialTableEvent());
    }

    loadReportData(tableEvent: TableEvent): void {
        const prevEvent = this.filterHolderService.getState(this.fraudReport.id, this.currentTab)?.tableEvent;
        this.tableEvent = mergeTableEvent(prevEvent, tableEvent);
        const activeTabState = this.filterHolderService.storeTableState(
            this.fraudReport.id,
            this.currentTab,
            this.tableEvent
        );
        this.fraudReportApiService.getFraudReportData(this.fraudReport.id, this.reportSection as FraudReportSection, {
            search: activeTabState.tableEvent?.filter?.query,
            createDateFrom: getFormattedDateOrNull(
                activeTabState.tableEvent?.filter?.separatedDateRange?.startDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            createDateTo: getFormattedDateOrNull(
                activeTabState.tableEvent?.filter?.separatedDateRange?.endDate?.date,
                DATE_TIME_SERVER_FORMAT,
            ),
            transactionTypes: fieldExtractor(activeTabState.tableEvent.filter?.transactionTypes) as TransactionType[],
            amountFrom: activeTabState.tableEvent?.filter?.amountRange?.from,
            amountTo: activeTabState.tableEvent?.filter?.amountRange?.to,
            currency: activeTabState.tableEvent?.filter?.amountRange?.currency,
            limit: activeTabState.tableEvent?.page?.pageSize,
            offset: activeTabState.tableEvent?.page?.pageOffset,
            sortBy: activeTabState.tableEvent?.sortBy?.columnName,
            sortOrder: activeTabState.tableEvent?.sortBy?.direction,
        }).pipe(rxSpinner(this.tableEvent.spinnerId)).subscribe((response) => {
            this.buildDataSource(response);
        });
    }

    formatTiynAsTenge(tiyn: number): string {
        if (!tiyn) {return '0';}
        const tenge = (tiyn / 100).toFixed(2);
        return tenge.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    private buildInitialTableEvent(): TableEvent {
        return {
            ...initialTableEvent,
            spinnerId: 'fraudReportData',
            filter: {
                query: '',
            },
            sortBy: {
                columnName: 'createdDate',
                direction: 'desc',
            },
        };
    }

    private navigateToIncident(incidentId: string): void {
        this.routerService.navigate(Path.INCIDENT_DETAILS, {
                pathParams: {
                    [INCIDENT_ID]: incidentId,
                },
                queryParams: {
                    [NESTED_TAB]: 'tabIncidentDetails',
                },
                queryParamsHandling: 'merge',
            },
        );
    }

    private navigateToTransactionDetails(incidentId: string, transactionId: string): void {
        this.routerService.navigate(Path.INCIDENTS_TRANSACTIONS_DETAILS, {
                pathParams: {
                    [INCIDENT_ID]: incidentId,
                    [TRANSACTION_ID]: transactionId,
                },
                queryParams: {
                    [NESTED_TAB]: 'tabTransactions',
                    [TRANSACTION_INDEX]: 0,
                },
                queryParamsHandling: 'merge',
            },
        );
    }

    private navigateToRefundDetails(refundId: string): void {
        this.routerService.navigate(Path.REFUND_DETAILS, {pathParams: {[REFUND_ID]: refundId}});
    }

    private buildDataSource(response: FraudReportDataPaginatedResponse): void {
        this.totalElements = response.total;
        this.totalAmount = response.totalAmount;
        this.dataSource = response.items.map(item => {
            const transactionStatus = findValueInDictionary(this.transactionStatuses, item.status, 'name_ru') ?? '-';
            const row: Row = {
                createdDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.transactionCreatedDate,
                },
                incidentId: <Column>{
                    type: ColumnType.LINK,
                    text: item.incidentId,
                    click: () => this.navigateToIncident(item.incidentId),
                },
                ...this.reportSection !== 'REFUNDS' && {
                    transactionId: <Column>{
                        type: ColumnType.LINK,
                        text: item.id,
                        click: () => this.navigateToTransactionDetails(item.incidentId, item.id),
                    }
                },
                ...this.reportSection === 'REFUNDS' && {
                    refundId: <Column>{
                        type: ColumnType.LINK,
                        text: item.refundId,
                        click: () => this.navigateToRefundDetails(item.refundId),
                    }
                },
                transactionDate: <Column>{
                    type: ColumnType.DATE_TIME_SECONDS,
                    sortable: true,
                    text: item.date,
                },
                ...this.reportSection === 'REFUNDS' && {
                    refundType: <Column>{
                        type: ColumnType.TEXT,
                        text: findValueInDictionary(this.refundTypes, item.refundType, 'name_ru') ?? '-',
                    }
                },
                ...this.reportSection !== 'REFUNDS' && {
                    transactionCategory: <Column>{
                        type: ColumnType.TEXT,
                        text: findValueInDictionary(this.transactionCategories, item.category, 'name_ru') ?? '-',
                    }
                },
                transactionType: <Column>{
                    type: ColumnType.TEXT,
                    text: findValueInDictionary(this.transactionTypes, item.type, 'name_ru') ?? '-',
                },
                ...this.reportSection === 'REFUNDS' && {
                    refundStatus: <StatusColumn>{
                        type: ColumnType.STATUS,
                        sortable: true,
                        text: findValueInDictionary(this.refundStatuses, item.refundStatus, 'name_ru') ?? '-',
                        style: mapRefundStatusToStyle(item.refundStatus as RefundStatuses),
                    },
                },
                ...this.reportSection !== 'REFUNDS' && {
                    status: <StatusColumn>{
                        type: ColumnType.STATUS,
                        sortable: true,
                        text: transactionStatus,
                        style: mapTransactionStatusToStyle(transactionStatus),
                    }
                },
                amount: item.amount == null
                    ? <Column>{
                        type: ColumnType.TEXT,
                        sortable: true,
                        text: '-',
                    }
                    : <Column>{
                        type: ColumnType.MONEY,
                        sortable: true,
                        text: item.amount.toString(),
                    },
                currency: <Column>{
                    type: ColumnType.TEXT,
                    sortable: true,
                    text: findValueInDictionary(this.currencies, item.currency, 'code1') ?? '-',
                },
                senderOrganization: <Column>{
                    type: ColumnType.TEXT,
                    sortable: true,
                    text: item.senderBin ?? '-',
                },
                receiverOrganization: <Column>{
                    type: ColumnType.TEXT,
                    sortable: true,
                    text: item.receiverBin ?? '-',
                },
            };
            return row;
        });
    }
}
